import React, { useState, useEffect } from 'react';
import Header from '../components/header';
import Footer from '../components/footer';
import { Helmet } from 'react-helmet'

const TextToSpeech = () => {
  const [text, setText] = useState('');
  const [rate, setRate] = useState(1);
  const [voices, setVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [showPlayer, setShowPlayer] = useState(false);

  const handleTextChange = (e) => setText(e.target.value);
  const handleRateChange = (e) => setRate(parseFloat(e.target.value));

  useEffect(() => {
    const loadVoices = () => {
      const availableVoices = window.speechSynthesis.getVoices();
      setVoices(availableVoices);
      const defaultVoice = availableVoices.find(v => v.lang === 'en-US') || availableVoices[0];
      setSelectedVoice(defaultVoice);
    };

    if (window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }

    loadVoices();
  }, []);

  const handlePlay = () => {
    if (!text.trim()) return;
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.voice = selectedVoice;
    utterance.rate = rate;
    window.speechSynthesis.speak(utterance);
    setShowPlayer(true);
  };

  return (
    <div>
      <Header />
      <Helmet>
  <html lang="en" />
  <title>Text to Speech Converter – Free Online Voice Generator | Tooliso</title>
  <meta
    name="description"
    content="Convert any text into natural-sounding speech using Tooliso’s free Text to Speech Converter. Download audio in MP3 or WAV formats instantly."
  />
  <link rel="canonical" href="https://tooliso.com/Text-to-Speech-Converter" />

  <meta property="og:title" content="Text to Speech – Convert Text to Voice Online | Tooliso" />
  <meta
    property="og:description"
    content="Generate lifelike voice from text with Tooliso's AI-powered Text to Speech tool. Perfect for learning, accessibility, and content creation."
  />
  <meta property="og:type" content="website" />
  <meta property="og:url" content="https://tooliso.com/Text-to-Speech-Converter" />

  <meta name="twitter:card" content="summary" />
  <meta name="twitter:title" content="Free Text to Speech Tool – Listen or Download Voice" />
  <meta
    name="twitter:description"
    content="Easily convert your text to audio and download the result. Tooliso’s text to speech tool is fast, simple, and free."
  />
</Helmet>
      <Header />
      <Header />
      <div className="tiso-tts-container">
        <div className="tiso-tts-card">
          <h1 className="tiso-tts-title">Free Online Text to Speech Converter</h1>
          <p className="tiso-tts-subtitle">
            Convert text to natural sounding audio instantly - No registration required
          </p>

          <div className="tiso-tts-controls">
            <div className="tiso-tts-input-group">
              <textarea
                id="tiso-tts-text"
                className="tiso-tts-textarea"
                placeholder="Enter your text here to convert to speech..."
                rows="6"
                value={text}
                onChange={handleTextChange}
              />
              <div className="tiso-tts-char-count">
                <span>{text.length}</span>/5000 characters
              </div>
            </div>

            <div className="tiso-tts-options">
              <div className="tiso-tts-option">
                <label htmlFor="tiso-tts-voice" className="tiso-tts-label">Voice:</label>
                <select
                  id="tiso-tts-voice"
                  className="tiso-tts-select"
                  value={selectedVoice?.name || ''}
                  onChange={(e) =>
                    setSelectedVoice(voices.find(v => v.name === e.target.value))
                  }
                >
                  {voices.map((v, idx) => (
                    <option key={idx} value={v.name}>
                      {v.name} ({v.lang})
                    </option>
                  ))}
                </select>
              </div>

              <div className="tiso-tts-option">
                <label htmlFor="tiso-tts-rate" className="tiso-tts-label">Speed:</label>
                <input
                  type="range"
                  id="tiso-tts-rate"
                  className="tiso-tts-range"
                  min="0.5"
                  max="2"
                  step="0.1"
                  value={rate}
                  onChange={handleRateChange}
                />
                <span className="tiso-tts-range-value">{rate.toFixed(1)}x</span>
              </div>
            </div>

            <div className="tiso-tts-buttons">
              <button
                id="tiso-tts-generate"
                className="tiso-tts-btn tiso-tts-btn-primary"
                onClick={handlePlay}
              >
                <i className="fas fa-play"></i> listen Audio
              </button>
            </div>

            {showPlayer && (
              <div id="tiso-tts-player-container" className="tiso-tts-player-container">
                <p>Playing via browser's speech synthesis...</p>
              </div>
            )}
          </div>

          <div className="tiso-tts-seo-content">
            <h2 className="tiso-tts-seo-title">Convert Text to Natural Sounding Speech Online for Free</h2>
            <p className="tiso-tts-seo-text">
              Our advanced text-to-speech converter lets you transform any written content into
              high-quality audio instantly. Perfect for listening to documents on the go. Our converter
              works entirely in your browser with no limits.
            </p>

            <div className="tiso-tts-faq">
              <h3 className="tiso-tts-faq-title">Frequently Asked Questions</h3>
              <div className="tiso-tts-faq-item">
                <h4 className="tiso-tts-faq-question">How do I convert text to speech for free?</h4>
                <p className="tiso-tts-faq-answer">
                  Simply type or paste your text into the box above, select your preferred voice and
                  speed, then click "Generate Audio" to listen to the speech.
                </p>
              </div>
              <div className="tiso-tts-faq-item">
                <h4 className="tiso-tts-faq-question">Can I download the audio?</h4>
                <p className="tiso-tts-faq-answer">
                  This tool is designed for instant listening only. For downloading audio, please use
                  other specialized tools.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TextToSpeech;
